import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Mercedes G-Class (Gelandewagen, W463) — an upright off-roader at true scale
// (1 unit = 1 metre): length ~4.6 including the tailgate spare (body ~4.4),
// width 1.76 (about 1.86 over the squared arch flares), height 1.97,
// wheelbase 2.85, on chunky 0.37-radius tyres. Nose is +Z. Signature cues:
// round amber indicator pods standing ON TOP of the front fenders, round
// headlamps in a flat front panel flanking a horizontal-slat grille with a
// chrome disc badge, a near-vertical windscreen, flat slab sides, a flat roof
// with rain-gutter ridges, rectangular cladding flares over the wheels, a
// full-size spare wheel on the upright tailgate, and vertical two-tier
// (red over amber) tail lamps beside it.

const PAINT = "#9aa1a8"; // silver-grey — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (pillars, grille base, brackets)
const CLAD = "#26282c"; // matte plastic cladding (flares, bumpers, sills)
const WELL = "#141518"; // wheel wells
const GLASS = "#22303e"; // tinted side / rear glazing
const SCREEN = "#31465a"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6";
const SILVER = "#adb3ba"; // grille slats, skid plate
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.38; // chunky off-road tyre
const WHEEL_X = 0.8; // half track (tyre outer faces proud of the flares)
const AXLE_F = 1.35;
const AXLE_R = -1.5; // wheelbase 2.85
const BODY_HW = 0.88; // slab body half width (1.76 over the panels)
const NOSE = 2.05; // flat front panel face
const TAIL = -2.15; // rear body face (tailgate/shell sits at -2.10)
// Greenhouse shell — one full-width paint box so the sides are slab-flat with
// the body (no shoulder step): front face at +0.60 (just behind the bonnet),
// back face at -2.10 (square, upright tailgate), sill at 1.02, roof line at 1.83.
const SHELL_ZF = 0.6;
const SHELL_ZB = -2.1;
const SHELL_Y0 = 1.02;
const SHELL_Y1 = 1.87; // roof line (roof top ~1.94, spec-tall: taller than wide)

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One chunky off-road wheel: wide tyre, brake disc, recessed dark dish, five
 *  bright spokes slightly proud of the tyre face, chrome centre cap. Every
 *  part shares `name` so the `drive` track spins them as one node. `side` is
 *  +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.34, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.18, 0.18, 0.03, 16, BRAKE, [x - side * 0.09, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.25, 0.25, 0.09, 18, "#202225", [x + side * 0.09, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.07, 0.44, 0.11, "#c4c9cf", [x + side * 0.145, y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.075, 0.075, 0.03, 12, CHROME, [x + side * 0.155, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well (a dark disc proud of the body side) plus the G's
 *  squared arch flare — a slim horizontal cladding box over the wheel and two
 *  short vertical caps wrapping its ends. No round brow: the flares on a
 *  W463 are rectangular blocks standing proud of the slab side. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.49, 0.49, 0.04, 18, WELL, [side * 0.87, WHEEL_R, z], [0, 0, Math.PI / 2]),
    box(0.12, 0.13, 1.06, CLAD, [side * (BODY_HW + 0.02), 0.83, z]), // squared flare top, proud of the slab
    box(0.12, 0.5, 0.13, CLAD, [side * (BODY_HW + 0.02), 0.62, z + 0.47]), // front flare wall
    box(0.12, 0.5, 0.13, CLAD, [side * (BODY_HW + 0.02), 0.62, z - 0.47]), // rear flare wall
  ];
}

// The four hazard indicators — the two iconic fender-top pods and the rear
// amber tiers. Parts and blink tracks are generated from one table so the
// pivots (each part's own centre) can never drift from the parts.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.79, 1.12, 1.9] },
  { name: "indicator-fr", pos: [0.79, 1.12, 1.9] },
  { name: "indicator-rl", pos: [-0.7, 1.0, -2.12] },
  { name: "indicator-rr", pos: [0.7, 1.0, -2.12] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.055, 0.055, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.5, 0.14, 3.2, TRIM, [0, 0.41, -0.075])); // underbody pan
  P.push(box(1.56, 0.14, 0.34, CLAD, [0, 0.4, 1.94])); // apron under the nose, back to the front wells
  P.push(box(1.56, 0.14, 0.24, CLAD, [0, 0.4, -2.06])); // apron under the tail, back to the rear wells

  // -- Body masses (paint): slab tub + flat bonnet below the fenders ----------
  P.push(paint(2 * BODY_HW, 0.52, NOSE - TAIL, [0, 0.76, -0.05])); // main tub, sill to bonnet line (top at 1.02)
  P.push(paint(1.46, 0.05, 1.5, [0, 1.025, 1.3])); // flat bonnet, 0.05 below the fender line
  for (const s of S) P.push(paint(0.17, 0.08, 1.5, [s * 0.805, 1.06, 1.3])); // flat fender tops

  // -- Greenhouse: one full-width slab shell, near-vertical screen, level roof --
  const shellCz = (SHELL_ZF + SHELL_ZB) / 2;
  const shellD = SHELL_ZF - SHELL_ZB;
  const shellCy = (SHELL_Y0 + SHELL_Y1) / 2;
  const shellH = SHELL_Y1 - SHELL_Y0;
  P.push(paint(2 * BODY_HW, shellH, shellD, [0, shellCy, shellCz])); // cabin shell, slab-flat with the body
  P.push(paint(2 * BODY_HW, 0.07, shellD, [0, 1.905, shellCz])); // flat, level roof, full slab width
  for (const s of S) P.push(paint(0.05, 0.035, shellD - 0.06, [s * 0.86, 1.955, shellCz])); // rain-gutter ridges
  // Windscreen: near-vertical (rake ~0.12), proud of the shell front face, with
  // a paint frame (the A-pillars) all around it.
  P.push(box(1.5, 0.5, 0.04, SCREEN, [0, 1.55, SHELL_ZF + 0.03], [-0.12, 0, 0]));
  for (const s of S) P.push(box(0.36, 0.015, 0.03, TRIM, [s * 0.3, 1.32, SHELL_ZF + 0.06], [-0.12, 0, 0])); // wipers
  // Side glass: a long proud panel on each slab side, split by B/C pillars.
  for (const s of S) {
    P.push(box(0.02, 0.46, 1.86, GLASS, [s * (BODY_HW + 0.008), 1.53, -0.62])); // long side glass row
    P.push(box(0.03, 0.48, 0.09, TRIM, [s * (BODY_HW + 0.012), 1.53, -0.05])); // B-pillar
    P.push(box(0.03, 0.48, 0.08, TRIM, [s * (BODY_HW + 0.012), 1.53, -0.95])); // C-pillar
    P.push(box(0.02, 0.025, 1.94, CHROME, [s * (BODY_HW + 0.01), 1.29, -0.6])); // belt-line strip
  }

  // -- Front fascia: round lamps, slat grille, disc badge, fender pods --------
  P.push(box(0.86, 0.26, 0.03, TRIM, [0, 0.88, 2.07])); // grille base
  for (let i = 0; i < 4; i++) {
    P.push(box(0.8, 0.035, 0.02, SILVER, [0, 0.775 + i * 0.07, 2.095])); // horizontal slats
  }
  P.push(cyl(0.07, 0.07, 0.02, 16, CHROME, [0, 0.88, 2.1], [Math.PI / 2, 0, 0])); // disc badge
  for (const s of S) {
    P.push(cyl(0.135, 0.135, 0.025, 16, TRIM, [s * 0.6, 0.88, 2.07], [Math.PI / 2, 0, 0])); // lamp bezel
    P.push(cyl(0.11, 0.11, 0.03, 16, LAMP, [s * 0.6, 0.88, 2.09], [Math.PI / 2, 0, 0])); // round headlamp
    P.push(cyl(0.028, 0.028, 0.035, 8, TRIM, [s * 0.79, 1.1, 1.9])); // pod base standing on the fender top
  }
  P.push(box(1.8, 0.18, 0.12, CLAD, [0, 0.5, 2.13])); // flat front bumper, narrower than the track
  P.push(box(0.7, 0.1, 0.08, SILVER, [0, 0.4, 2.17])); // skid-plate hint
  P.push(box(0.44, 0.12, 0.02, PLATE, [0, 0.52, 2.2])); // front plate, proud of the bumper

  // -- Rear fascia: square upright tailgate, spare on it, two-tier lamps ------
  P.push(box(1.34, 0.5, 0.04, GLASS, [0, 1.55, SHELL_ZB - 0.03], [0.05, 0, 0])); // near-vertical rear screen, proud of the tailgate
  P.push(box(0.24, 0.025, 0.02, RED, [0, 1.84, SHELL_ZB - 0.03])); // high brake light on the tailgate top
  for (const s of S) {
    P.push(box(0.16, 0.13, 0.05, RED, [s * 0.7, 1.12, -2.12])); // tail lamp, red tier, proud of the tailgate
    P.push(box(0.03, 0.1, 0.05, TRIM, [s * 0.84, 1.06, -2.115])); // corner trim beside the lamp
  }
  P.push(box(0.28, 0.4, 0.08, TRIM, [0.15, 1.2, -2.14])); // spare carrier bracket on the tailgate
  P.push(cyl(WHEEL_R, WHEEL_R, 0.26, 18, TIRE, [0.15, 1.2, -2.27], [Math.PI / 2, 0, 0])); // spare tyre (does not spin)
  P.push(cyl(0.21, 0.21, 0.06, 16, "#202225", [0.15, 1.2, -2.38], [Math.PI / 2, 0, 0])); // spare dish, rear-facing
  P.push(cyl(0.065, 0.065, 0.03, 12, CHROME, [0.15, 1.2, -2.41], [Math.PI / 2, 0, 0])); // spare centre cap
  P.push(box(1.8, 0.16, 0.1, CLAD, [0, 0.5, -2.16])); // flat rear bumper
  P.push(box(0.06, 0.06, 0.06, CHROME, [0.45, 0.46, -2.21])); // tow-hook hint
  P.push(box(0.4, 0.12, 0.02, PLATE, [-0.42, 0.68, -2.145])); // rear plate beside the spare

  // -- Side furniture ----------------------------------------------------------
  for (const s of S) {
    P.push(box(0.08, 0.07, 1.88, CLAD, [s * 0.89, 0.52, -0.075])); // running strip, well to well
    P.push(box(0.03, 0.03, 0.16, CHROME, [s * 0.895, 1.14, 0.32])); // front door handle
    P.push(box(0.03, 0.03, 0.16, CHROME, [s * 0.895, 1.14, -0.62])); // rear door handle
    P.push(box(0.1, 0.03, 0.04, TRIM, [s * 0.91, 1.3, 0.42])); // mirror arm
    P.push(paint(0.09, 0.1, 0.15, [s * 0.985, 1.32, 0.4])); // mirror cap
    P.push(box(0.08, 0.08, 0.012, CHROME, [s * 0.985, 1.32, 0.318])); // mirror glass
  }

  // The hazard indicators: amber pods standing on the front fender tops (THE
  // G-Class icon) and the amber tier of each tail lamp.
  for (const ind of INDICATORS) {
    P.push(
      ind.pos[2] > 0
        ? cyl(0.055, 0.055, 0.06, 12, AMBER, ind.pos, undefined, { name: ind.name })
        : box(0.16, 0.11, 0.05, AMBER, ind.pos, undefined, { name: ind.name }),
    );
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const mercedesGClass = defineModel({
  id: "mercedes-g-class",
  name: "Mercedes G-Class",
  batchId: "cars",
  tags: ["car", "mercedes", "g-class", "suv", "offroad"],
  summary: "A Mercedes G-Class off-roader at true scale, in silver-grey.",
  description:
    "A Mercedes G-Class (Gelandewagen, W463) at real 1 unit = 1 metre scale " +
    "(about 4.6 long over the tailgate spare, 1.76 wide, 1.97 tall, 2.85 " +
    "wheelbase, chunky 0.37-radius tyres). All the W463 cues: round amber " +
    "indicator pods on the front fender tops, round headlamps flanking a " +
    "slatted grille with a chrome disc badge, a near-vertical windscreen, " +
    "flat slab sides with squared cladding flares, a flat roof with " +
    "rain-gutter ridges, a full-size spare on the upright tailgate and " +
    "vertical two-tier tail lamps beside it. Body panels carry the 'paint' " +
    "recolour slot (silver-grey); `drive` spins the four road wheels (the " +
    "spare stays still) and `hazards` blinks the fender pods and rear ambers.",
  author: "Fable 5",
  footprint: 2.45,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
